import { useState, useEffect, useRef, createElement } from 'react';

/**
 * Fixed-position toast notification that appears briefly when a barcode is
 * scanned.  Shows a green success banner or a red error banner, then
 * auto-dismisses after 3 seconds with a fade-out transition.
 *
 * @param {Object}      props
 * @param {Object|null} props.lastScan           - Latest scan result from useBarcode.
 * @param {string}      props.lastScan.code      - The scanned barcode string.
 * @param {Object|null} props.lastScan.product   - The matched product (null on error).
 * @param {string|null} props.lastScan.error     - Error message (null on success).
 * @param {number}      props.lastScan.timestamp - Epoch ms when the scan occurred.
 */

const DISPLAY_DURATION = 3000; // ms to show the toast
const FADE_DURATION = 300;     // ms for the fade-out transition

export default function ScanIndicator({ lastScan }) {
  const [visible, setVisible] = useState(false);
  const [fading, setFading] = useState(false);
  const [displayScan, setDisplayScan] = useState(null);
  const fadeTimerRef = useRef(null);
  const hideTimerRef = useRef(null);

  useEffect(() => {
    if (!lastScan || !lastScan.timestamp) {
      return;
    }

    // Clear any existing timers from a previous scan.
    if (fadeTimerRef.current) {
      clearTimeout(fadeTimerRef.current);
      fadeTimerRef.current = null;
    }
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }

    // Show the toast immediately with the new scan data.
    setDisplayScan(lastScan);
    setFading(false);
    setVisible(true);

    // Start fade-out after the display duration.
    fadeTimerRef.current = setTimeout(() => {
      setFading(true);

      // Remove from DOM after the CSS transition completes.
      hideTimerRef.current = setTimeout(() => {
        setVisible(false);
        setFading(false);
      }, FADE_DURATION);
    }, DISPLAY_DURATION);

    return () => {
      if (fadeTimerRef.current) {
        clearTimeout(fadeTimerRef.current);
      }
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
    };
  }, [lastScan?.timestamp]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!visible || !displayScan) {
    return null;
  }

  const isSuccess = displayScan.product && !displayScan.error;

  return (
    <div
      role="status"
      aria-live="polite"
      className={
        'fixed bottom-4 right-4 z-50 flex max-w-sm items-center gap-3 rounded-lg px-4 py-3 shadow-lg ' +
        'transition-opacity duration-300 ease-in-out ' +
        (isSuccess
          ? 'bg-pos-success text-white'
          : 'bg-pos-danger text-white') +
        (fading ? ' opacity-0' : ' opacity-100')
      }
      style={{ pointerEvents: 'none' }}
    >
      {/* Icon */}
      {isSuccess ? (
        <svg
          className="h-5 w-5 flex-shrink-0"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ) : (
        <svg
          className="h-5 w-5 flex-shrink-0"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      )}

      {/* Message */}
      <span className="text-sm font-medium leading-snug">
        {isSuccess
          ? `${displayScan.product.name} added to cart`
          : `Product not found: ${displayScan.code}`}
      </span>
    </div>
  );
}
